// Per-dialect synthesizer for index-lifecycle ops (v2 Slice C5).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice C5.
//
// Per-engine surface (REBUILD / REORGANIZE / REINDEX / clustered-swap):
//   - mssql:  ALTER INDEX <name> ON <tbl> REBUILD [WITH (...)] / REORGANIZE
//             CREATE [UNIQUE] [CLUSTERED] INDEX <name> ON <tbl> (cols)
//                 WITH (DROP_EXISTING = ON)              — clustered swap
//   - pgsql:  REINDEX INDEX <name> | REINDEX TABLE <name>
//             rebuild routes to REINDEX INDEX (the canonical pgsql idiom).
//             reorganize / clustered-swap throw — pgsql has no equivalent.
//   - mysql:  rebuild   → ALTER TABLE <tbl> ENGINE = InnoDB (table-level)
//             reorganize→ OPTIMIZE TABLE <tbl> (table-level)
//             reindex / clustered-swap throw.
//   - oracle: rebuild   → ALTER INDEX <name> REBUILD [ONLINE] [TABLESPACE …]
//             reorganize→ ALTER INDEX <name> COALESCE
//             reindex / clustered-swap throw.
//   - sqlite: reindex   → REINDEX <name> (ignores `target` distinction —
//                          sqlite's REINDEX accepts any name in that slot)
//             rebuild   → REINDEX <name> (sqlite's only rebuild path)
//             reorganize / clustered-swap throw.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { IndexLifecycleOp } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

function qnText(engine: SqlEngine, qn: QualifiedName): string {
  if (qn.raw && qn.raw.length > 0) return qn.raw;
  const name = maybeQuoteIdent(engine, qn.name, qn.quoted);
  if (qn.schema) {
    return `${maybeQuoteIdent(engine, qn.schema, qn.quoted)}.${name}`;
  }
  return name;
}

function require_<T>(value: T | undefined, engine: SqlEngine, kind: string, field: string): T {
  if (value === undefined) {
    throw new Error(
      `applyDdlEdit: ${engine} index-lifecycle '${kind}' requires '${field}'`,
    );
  }
  return value;
}

function rejectField(engine: SqlEngine, kind: string, field: string): never {
  throw new Error(
    `applyDdlEdit: ${engine} index-lifecycle '${kind}' does not accept '${field}'`,
  );
}

function unsupported(engine: SqlEngine, kind: string, hint: string): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support index-lifecycle '${kind}' — ${hint}`,
  );
}

export function synthesizeIndexLifecycle(
  engine: SqlEngine,
  lifecycle: IndexLifecycleOp,
): string {
  if (lifecycle.kind === 'rebuild') return synthRebuild(engine, lifecycle);
  if (lifecycle.kind === 'reorganize') return synthReorganize(engine, lifecycle);
  if (lifecycle.kind === 'reindex') return synthReindex(engine, lifecycle);
  return synthClusteredSwap(engine, lifecycle);
}

function synthRebuild(
  engine: SqlEngine,
  l: Extract<IndexLifecycleOp, { kind: 'rebuild' }>,
): string {
  if (engine === 'mssql') {
    const idx = require_(l.indexName, engine, 'rebuild', 'indexName');
    const tbl = require_(l.table, engine, 'rebuild', 'table');
    if (l.oracleOnline !== undefined) rejectField(engine, 'rebuild', 'oracleOnline');
    if (l.oracleTablespace !== undefined) rejectField(engine, 'rebuild', 'oracleTablespace');
    const opts = l.mssqlOptions && l.mssqlOptions.length > 0
      ? ` WITH (${l.mssqlOptions})`
      : '';
    return `ALTER INDEX ${qnText(engine, idx)} ON ${qnText(engine, tbl)} REBUILD${opts}`;
  }
  if (engine === 'postgres') {
    const idx = require_(l.indexName, engine, 'rebuild', 'indexName');
    if (l.mssqlOptions !== undefined) rejectField(engine, 'rebuild', 'mssqlOptions');
    if (l.oracleOnline !== undefined) rejectField(engine, 'rebuild', 'oracleOnline');
    if (l.oracleTablespace !== undefined) rejectField(engine, 'rebuild', 'oracleTablespace');
    return `REINDEX INDEX ${qnText(engine, idx)}`;
  }
  if (engine === 'mysql') {
    const tbl = require_(l.table, engine, 'rebuild', 'table');
    if (l.mssqlOptions !== undefined) rejectField(engine, 'rebuild', 'mssqlOptions');
    if (l.oracleOnline !== undefined) rejectField(engine, 'rebuild', 'oracleOnline');
    if (l.oracleTablespace !== undefined) rejectField(engine, 'rebuild', 'oracleTablespace');
    return `ALTER TABLE ${qnText(engine, tbl)} ENGINE = InnoDB`;
  }
  if (engine === 'oracle') {
    const idx = require_(l.indexName, engine, 'rebuild', 'indexName');
    if (l.mssqlOptions !== undefined) rejectField(engine, 'rebuild', 'mssqlOptions');
    const online = l.oracleOnline ? ' ONLINE' : '';
    const ts = l.oracleTablespace ? ` TABLESPACE ${l.oracleTablespace}` : '';
    return `ALTER INDEX ${qnText(engine, idx)} REBUILD${online}${ts}`;
  }
  // sqlite
  const idx = require_(l.indexName, engine, 'rebuild', 'indexName');
  if (l.mssqlOptions !== undefined) rejectField(engine, 'rebuild', 'mssqlOptions');
  if (l.oracleOnline !== undefined) rejectField(engine, 'rebuild', 'oracleOnline');
  if (l.oracleTablespace !== undefined) rejectField(engine, 'rebuild', 'oracleTablespace');
  return `REINDEX ${qnText(engine, idx)}`;
}

function synthReorganize(
  engine: SqlEngine,
  l: Extract<IndexLifecycleOp, { kind: 'reorganize' }>,
): string {
  if (engine === 'mssql') {
    const idx = require_(l.indexName, engine, 'reorganize', 'indexName');
    const tbl = require_(l.table, engine, 'reorganize', 'table');
    return `ALTER INDEX ${qnText(engine, idx)} ON ${qnText(engine, tbl)} REORGANIZE`;
  }
  if (engine === 'mysql') {
    const tbl = require_(l.table, engine, 'reorganize', 'table');
    return `OPTIMIZE TABLE ${qnText(engine, tbl)}`;
  }
  if (engine === 'oracle') {
    const idx = require_(l.indexName, engine, 'reorganize', 'indexName');
    return `ALTER INDEX ${qnText(engine, idx)} COALESCE`;
  }
  if (engine === 'postgres') {
    unsupported(engine, 'reorganize',
      `pgsql has no per-index reorganize. Use kind:'rebuild' to emit REINDEX INDEX, or `
        + `'VACUUM' / 'CLUSTER' table-level commands out-of-band.`);
  }
  // sqlite
  unsupported(engine, 'reorganize',
    `sqlite has no reorganize concept. Use kind:'reindex' or kind:'rebuild' instead.`);
}

function synthReindex(
  engine: SqlEngine,
  l: Extract<IndexLifecycleOp, { kind: 'reindex' }>,
): string {
  if (engine === 'postgres') {
    const keyword = l.target === 'table' ? 'TABLE' : 'INDEX';
    return `REINDEX ${keyword} ${qnText(engine, l.name)}`;
  }
  if (engine === 'sqlite') {
    return `REINDEX ${qnText(engine, l.name)}`;
  }
  if (engine === 'mssql') {
    unsupported(engine, 'reindex',
      `mssql has no REINDEX command. Use kind:'rebuild' to emit ALTER INDEX … REBUILD.`);
  }
  if (engine === 'mysql') {
    unsupported(engine, 'reindex',
      `mysql has no REINDEX command. Use kind:'rebuild' (ALTER TABLE … ENGINE = InnoDB) `
        + `or kind:'reorganize' (OPTIMIZE TABLE) instead.`);
  }
  // oracle
  unsupported(engine, 'reindex',
    `oracle has no REINDEX command. Use kind:'rebuild' to emit ALTER INDEX … REBUILD.`);
}

function synthClusteredSwap(
  engine: SqlEngine,
  l: Extract<IndexLifecycleOp, { kind: 'clustered-swap' }>,
): string {
  if (engine !== 'mssql') {
    unsupported(engine, 'clustered-swap',
      `the WITH (DROP_EXISTING = ON) idiom is mssql-only. On pgsql / oracle / sqlite, `
        + `emit a drop+create pair (DROP INDEX <old>; CREATE INDEX <new>); on mysql, `
        + `rebuild the table with kind:'rebuild' (ALTER TABLE … ENGINE = InnoDB).`);
  }
  if (l.columns.length === 0) {
    throw new Error(
      `applyDdlEdit: mssql index-lifecycle 'clustered-swap' requires at least one column`,
    );
  }
  const unique = l.unique ? 'UNIQUE ' : '';
  const clustered = l.clustered === false ? '' : 'CLUSTERED ';
  const cols = l.columns.join(', ');
  return `CREATE ${unique}${clustered}INDEX ${qnText('mssql', l.indexName)} ON `
    + `${qnText('mssql', l.table)} (${cols}) WITH (DROP_EXISTING = ON)`;
}
